import React, { useState, useRef, useCallback, useEffect } from 'react';
import clsx from 'clsx';

interface ScrollIndicatorProps {
  totalCards: number;
  currentIndex: number;
  onScrollToCard: (index: number) => void;
  className?: string;
}

export function ScrollIndicator({
  totalCards,
  currentIndex,
  onScrollToCard,
  className
}: ScrollIndicatorProps) {
  const [isLongPress, setIsLongPress] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [targetIndex, setTargetIndex] = useState(currentIndex);

  const indicatorRef = useRef<HTMLDivElement>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startPositionRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);

  // Constants
  const LONG_PRESS_DURATION = 200; // ms
  const DOT_SIZE = 8; // pixels
  const DOT_SPACING = 4; // pixels between dots

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = indicatorRef.current?.getBoundingClientRect();
    if (!rect) return;

    startPositionRef.current = {
      x: e.clientX,
      y: e.clientY
    };


    // Start long press timer
    longPressTimerRef.current = setTimeout(() => {
      setIsLongPress(true);
      setIsDragging(true);
      isDraggingRef.current = true;

      // Add haptic feedback if available
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }
    }, LONG_PRESS_DURATION);

    // Capture pointer to handle move/up events even outside the component
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDraggingRef.current || !indicatorRef.current) return;

    const rect = indicatorRef.current.getBoundingClientRect();
    const relativeY = e.clientY - rect.top;

    // Calculate which card index the drag position corresponds to
    const indicatorHeight = rect.height;
    const dotHeight = DOT_SIZE + DOT_SPACING;
    const totalDotsHeight = totalCards * dotHeight - DOT_SPACING;
    const startY = (indicatorHeight - totalDotsHeight) / 2;

    // Map drag position to card index
    const dragIndex = Math.round((relativeY - startY) / dotHeight);
    const clampedIndex = Math.max(0, Math.min(dragIndex, totalCards - 1));

    if (clampedIndex !== targetIndex) {
      setTargetIndex(clampedIndex);
      onScrollToCard(clampedIndex);

      // Light haptic feedback for index changes
      if ('vibrate' in navigator) {
        navigator.vibrate(20);
      }
    }

  }, [totalCards, targetIndex, onScrollToCard]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    // Clear long press timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    // If it was just a regular click (not a long press), navigate to that card
    if (!isLongPress && !isDraggingRef.current) {
      const rect = indicatorRef.current?.getBoundingClientRect();
      if (rect) {
        const relativeY = e.clientY - rect.top;
        const indicatorHeight = rect.height;
        const dotHeight = DOT_SIZE + DOT_SPACING;
        const totalDotsHeight = totalCards * dotHeight - DOT_SPACING;
        const startY = (indicatorHeight - totalDotsHeight) / 2;

        const clickedIndex = Math.round((relativeY - startY) / dotHeight);
        const clampedIndex = Math.max(0, Math.min(clickedIndex, totalCards - 1));
        onScrollToCard(clampedIndex);
      }
    }

    // Reset state
    setIsLongPress(false);
    setIsDragging(false);
    isDraggingRef.current = false;
    setTargetIndex(currentIndex);

    // Release pointer capture
    e.currentTarget.releasePointerCapture(e.pointerId);
  }, [isLongPress, totalCards, onScrollToCard, currentIndex]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  // Update target index when currentIndex changes externally
  useEffect(() => {
    if (!isDragging) {
      setTargetIndex(currentIndex);
    }
  }, [currentIndex, isDragging]);

  const renderDots = () => {
    return Array.from({ length: totalCards }, (_, index) => {
      const isActive = currentIndex === index;
      const isTarget = isDragging && targetIndex === index;

      return (
        <div
          key={index}
          className={clsx(
            "rounded-full transition-all duration-150 flex-shrink-0",
            {
              // Normal state
              "h-2 w-2 bg-blue-500": isActive && !isDragging,
              "h-2 w-2 bg-gray-300": !isActive && !isDragging,

              // Fast scroll state
              "h-3 w-3 bg-blue-500 ring-2 ring-blue-200": isActive && isDragging,
              "h-3 w-3 bg-blue-400 ring-1 ring-blue-100": isTarget && isDragging && !isActive,
              "h-2 w-2 bg-gray-200": !isActive && !isTarget && isDragging,
            }
          )}
          style={{
            marginBottom: index < totalCards - 1 ? `${DOT_SPACING}px` : 0
          }}
        />
      );
    });
  };

  return (
    <div
      ref={indicatorRef}
      className={clsx(
        "fixed top-1/2 right-3 transform -translate-y-1/2 z-50 touch-none select-none",
        "flex flex-col items-center justify-center px-1 py-2 rounded-full transition-all transform-gpu duration-200",
        {
          "bg-white border shadow-lg": !isDragging,
          "bg-blue-50 border-2 border-blue-200 shadow-xl scale-110": isDragging,
        },
        className
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {renderDots()}

      {/* Fast scroll overlay */}
      {isDragging && (
        <div className="absolute left-full ml-2 bg-blue-900 text-white px-2 py-1 rounded text-sm font-medium whitespace-nowrap pointer-events-none">
          Card {targetIndex + 1} of {totalCards}
        </div>
      )}
    </div>
  );
}